import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, Target, AlertCircle, 
  CheckCircle, Clock, BarChart3, Activity, Briefcase, Phone,
  Shield, Megaphone, Eye, ChevronRight, ArrowUp, ArrowDown,
  AlertTriangle, Loader2, Gauge
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PresidentDashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [scorecards, setScorecards] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [period]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API}/executive/president/dashboard?period=${period}`, { headers });
      setData(response.data.data);
      setAlerts(response.data.alerts || []);
      setBottlenecks(response.data.bottlenecks || []);
      setScorecards(response.data.department_scorecards || null);
    } catch (error) {
      console.error('Error loading President dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Access restricted to executive roles only');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null) return '0%';
    return `${value.toFixed(1)}%`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_target': return 'text-green-600 bg-green-100 dark:bg-green-900/30';
      case 'below_target': return 'text-red-600 bg-red-100 dark:bg-red-900/30';
      case 'needs_attention': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30';
    }
  };

  const getAlertBadgeVariant = (priority) => {
    switch (priority) {
      case 'critical': return 'destructive';
      case 'high': return 'default';
      default: return 'secondary';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-orange-500';
      default: return 'bg-yellow-500';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading President Dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 p-6" data-testid="president-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Gauge className="h-8 w-8 text-primary" />
              President Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Execution & Accountability - Operational oversight
            </p>
          </div>
          <div className="flex gap-2">
            {['today', 'week', 'month', 'quarter', 'ytd'].map((p) => (
              <Button
                key={p}
                variant={period === p ? 'default' : 'outline'}
                onClick={() => setPeriod(p)}
                size="sm"
              >
                {p === 'ytd' ? 'YTD' : p.charAt(0).toUpperCase() + p.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Alerts & Bottlenecks Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Critical Alerts */}
          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
                Alerts & Red Flags
                <Badge variant="destructive">{alerts.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p>No critical alerts</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {alerts.map((alert, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Badge variant={getAlertBadgeVariant(alert.priority)} className="text-xs">
                          {alert.priority}
                        </Badge>
                        <div>
                          <p className="font-medium text-sm">{alert.title}</p>
                          <p className="text-xs text-muted-foreground">{alert.category}</p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => alert.link && navigate(alert.link)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bottlenecks */}
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
                <AlertTriangle className="h-5 w-5" />
                Operational Bottlenecks
                <Badge variant="outline">{bottlenecks.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bottlenecks.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p>No bottlenecks detected</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {bottlenecks.map((bottleneck, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-8 rounded ${getSeverityColor(bottleneck.severity)}`}></div>
                        <div>
                          <p className="font-medium text-sm capitalize">{bottleneck.stage?.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-muted-foreground">
                            {bottleneck.job_count} jobs (avg: {bottleneck.avg_count})
                          </p>
                        </div>
                      </div>
                      <Badge variant={bottleneck.severity === 'high' ? 'destructive' : 'outline'}>
                        {bottleneck.severity}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Department Scorecards */}
        {scorecards && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Department Scorecards
              </CardTitle>
              <CardDescription>Performance vs targets across departments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Sales Scorecard */}
                {scorecards.sales && (
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        Sales
                      </h3>
                      <Badge className={getStatusColor(scorecards.sales.overall_status)}>
                        {scorecards.sales.overall_status === 'on_target' ? 'On Track' : 'Needs Attention'}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(scorecards.sales.metrics || {}).map(([key, metric]) => (
                        <div key={key}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="font-medium">
                              {metric.unit === '$' ? formatCurrency(metric.value) : 
                               metric.unit === '%' ? formatPercent(metric.value) : metric.value}
                            </span>
                          </div>
                          {metric.target && (
                            <Progress 
                              value={Math.min((metric.value / metric.target) * 100, 100)} 
                              className="h-1.5"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <Button 
                      variant="link" 
                      className="mt-3 p-0 h-auto" 
                      onClick={() => navigate('/sales/dashboard')}
                    >
                      View Details <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                {/* Operations Scorecard */}
                {scorecards.operations && (
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        Operations
                      </h3>
                      <Badge className={getStatusColor(scorecards.operations.overall_status)}>
                        {scorecards.operations.overall_status === 'on_target' ? 'On Track' : 'Needs Attention'}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(scorecards.operations.metrics || {}).map(([key, metric]) => (
                        <div key={key}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="font-medium">
                              {metric.unit === '$' ? formatCurrency(metric.value) : 
                               metric.unit === '%' ? formatPercent(metric.value) : 
                               `${metric.value}${metric.unit ? ' ' + metric.unit : ''}`}
                            </span>
                          </div>
                          {metric.target && metric.status !== 'informational' && (
                            <Progress 
                              value={key === 'avg_cycle_time' 
                                ? Math.max(0, 100 - ((metric.value / metric.target) * 100 - 100))
                                : Math.min((metric.value / metric.target) * 100, 100)} 
                              className="h-1.5"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <Button 
                      variant="link" 
                      className="mt-3 p-0 h-auto" 
                      onClick={() => navigate('/operations/dashboard')}
                    >
                      View Details <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                {/* Call Center Scorecard */}
                {scorecards.call_center && (
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        Call Center
                      </h3>
                      <Badge className={getStatusColor(scorecards.call_center.overall_status)}>
                        {scorecards.call_center.overall_status === 'on_target' ? 'On Track' : 'Needs Attention'}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(scorecards.call_center.metrics || {}).map(([key, metric]) => (
                        <div key={key}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="font-medium">
                              {metric.unit === '$' ? formatCurrency(metric.value) : 
                               metric.unit === '%' ? formatPercent(metric.value) : 
                               `${metric.value}${metric.unit ? metric.unit : ''}`}
                            </span>
                          </div>
                          {metric.target && (
                            <Progress 
                              value={key === 'missed_call_rate' || key === 'avg_response_time'
                                ? Math.max(0, 100 - ((metric.value / metric.target) * 100 - 100))
                                : Math.min((metric.value / metric.target) * 100, 100)} 
                              className="h-1.5"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <Button 
                      variant="link" 
                      className="mt-3 p-0 h-auto" 
                      onClick={() => navigate('/call-center')}
                    >
                      View Details <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                {/* Marketing Scorecard */}
                {scorecards.marketing && (
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Megaphone className="h-4 w-4" />
                        Marketing
                      </h3>
                      {scorecards.marketing.data_status === 'limited' ? (
                        <Badge variant="outline">Limited Data</Badge>
                      ) : (
                        <Badge className={getStatusColor(scorecards.marketing.overall_status)}>
                          {scorecards.marketing.overall_status === 'on_target' ? 'On Track' : 'Needs Attention'}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-3">
                      {Object.entries(scorecards.marketing.metrics || {}).map(([key, metric]) => (
                        <div key={key}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="font-medium">{metric.value}</span>
                          </div>
                          {metric.target && (
                            <Progress 
                              value={Math.min((metric.value / metric.target) * 100, 100)} 
                              className="h-1.5"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <Button 
                      variant="link" 
                      className="mt-3 p-0 h-auto" 
                      onClick={() => navigate('/marketing/dashboard')}
                    >
                      View Details <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Key Metrics Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Revenue (MTD)</p>
                  <p className="text-2xl font-bold">{formatCurrency(data?.revenue?.total_revenue_mtd)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Projects</p>
                  <p className="text-2xl font-bold">{data?.operations?.active_projects || 0}</p>
                </div>
                <Briefcase className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pipeline Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(data?.sales?.pipeline_value)}</p>
                </div>
                <Target className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">EOS Rocks On Track</p>
                  <p className="text-2xl font-bold">{data?.eos?.rocks?.on_track || 0}/{data?.eos?.rocks?.total || 0}</p>
                </div>
                <Shield className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/dashboard/ceo')}>
                <Eye className="h-6 w-6" />
                <span>CEO View</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/sales/dashboard')}>
                <Target className="h-6 w-6" />
                <span>Sales Hub</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/operations/dashboard')}>
                <Briefcase className="h-6 w-6" />
                <span>Operations</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/call-center')}>
                <Phone className="h-6 w-6" />
                <span>Call Center</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/eos/dashboard')}>
                <Shield className="h-6 w-6" />
                <span>EOS Hub</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/settings')}>
                <Users className="h-6 w-6" />
                <span>Settings</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PresidentDashboard;
